import { api } from "lib/graphql/api"
import { query } from "lib/graphql/query"
import { mutate } from "lib/graphql/mutate"
import { gql } from "@apollo/client"

export const getMaintenanceTask = api(async function getMaintenanceTask(id) {
    if (!id || id === "new") return null

    return query(
        gql`
            query GetMaintenanceTask($id: ID!) {
                getMaintenanceTask(id: $id) {
                    id
                    categoryId
                    categoryPath
                    heading
                    classification
                    frequency {
                        period
                        interval
                        label
                    }
                    frequencyCode
                    skillSet
                    minutes
                    unitOfMeasure
                    version
                    actionsHtml
                    notesHtml
                    authorsNotesHtml
                    componentInstructionsHtml
                    internalNotes {
                        text
                        timingAdjustment {
                            minutes
                            appliesToFrequencyCode
                            context
                        }
                    }
                    relationships {
                        type
                        target
                        note
                    }
                    compliance {
                        code
                        title
                        type
                    }
                    tags
                    taskType
                    timing
                    draft
                    warnings
                    scheduleReferences {
                        code
                        title
                    }
                    hyperlinksToAdd
                }
            }
        `,
        { id },
        { returns: "getMaintenanceTask", fetchPolicy: "network-only" }
    )
})

export const getMaintenanceTasks = api(async function getMaintenanceTasks({
    query: q,
    skip,
    take,
    orderStmt,
    search,
    categoryId,
    hasClassification,
    hasFrequency,
    hasSkillSet,
    draftsOnly,
    excludeDrafts,
} = {}) {
    if (!q && !search && !categoryId && !draftsOnly) return { items: [], totalCount: 0 }

    return query(
        gql`
            query GetMaintenanceTasks(
                $query: JSON
                $skip: Int
                $take: Int
                $orderStmt: [String]
                $search: String
                $categoryId: String
                $hasClassification: Boolean
                $hasFrequency: Boolean
                $hasSkillSet: Boolean
                $draftsOnly: Boolean
                $excludeDrafts: Boolean
            ) {
                getMaintenanceTasks(
                    query: $query
                    skip: $skip
                    take: $take
                    orderStmt: $orderStmt
                    search: $search
                    categoryId: $categoryId
                    hasClassification: $hasClassification
                    hasFrequency: $hasFrequency
                    hasSkillSet: $hasSkillSet
                    draftsOnly: $draftsOnly
                    excludeDrafts: $excludeDrafts
                ) {
                    items {
                        id
                        categoryPath
                        heading
                        classification
                        frequencyCode
                        skillSet
                        minutes
                        unitOfMeasure
                        version
                        actionsHtml
                        notesHtml
                        authorsNotesHtml
                        componentInstructionsHtml
                        taskType
                        timing
                        draft
                    }
                    totalCount
                }
            }
        `,
        {
            query: q,
            skip,
            take,
            orderStmt,
            search,
            categoryId,
            hasClassification,
            hasFrequency,
            hasSkillSet,
            draftsOnly,
            excludeDrafts,
        },
        { returns: "getMaintenanceTasks" }
    )
})

// Count only, for the reviewer banner. Deliberately not the drafts list query above, which
// returns every component body — too much work for a single number on every library page load.
// Returns 0 for users who cannot review, so this is safe to call for anyone.
export const getDraftComponentCount = api(async function getDraftComponentCount() {
    return query(
        gql`
            query GetDraftComponentCount {
                getDraftComponentCount
            }
        `,
        {},
        { returns: "getDraftComponentCount" }
    )
})

// excludeDrafts is opt-in: the component library shows drafts in the folder they were added to,
// while the authoring component picker must never offer them for selection.
export const getMaintenanceCategory = api(async function getMaintenanceCategory(
    idOrOptions,
    legacyExcludeDrafts = false
) {
    const { id, excludeDrafts } = getCategoryQuery(idOrOptions, legacyExcludeDrafts)
    if (!id) return null

    return query(
        gql`
            query GetMaintenanceCategory($id: ID!, $excludeDrafts: Boolean) {
                getMaintenanceCategory(id: $id) {
                    id
                    name
                    type
                    parentId
                    path
                    originalText
                    children {
                        id
                        name
                        type
                        parentId
                        path
                        taskCount
                    }
                    tasks(excludeDrafts: $excludeDrafts) {
                        id
                        heading
                        classification
                        frequencyCode
                        skillSet
                        taskType
                        timing
                        actionsHtml
                        notesHtml
                        authorsNotesHtml
                        componentInstructionsHtml
                        draft
                    }
                }
            }
        `,
        { id, excludeDrafts },
        { returns: "getMaintenanceCategory" }
    )
})

function getCategoryQuery(idOrOptions, legacyExcludeDrafts) {
    if (idOrOptions && typeof idOrOptions === "object") return idOrOptions
    return { id: idOrOptions, excludeDrafts: legacyExcludeDrafts }
}

export const getHistoricalMaintenanceCategory = api(async function getHistoricalMaintenanceCategory({ id, asOf } = {}) {
    if (!id || !asOf) return null

    return query(
        gql`
            query GetHistoricalMaintenanceCategory($id: ID!, $asOf: String!) {
                getHistoricalMaintenanceCategory(id: $id, asOf: $asOf) {
                    id
                    name
                    type
                    parentId
                    path
                    originalText
                    children {
                        id
                        name
                        type
                        parentId
                        path
                        taskCount
                    }
                    tasks {
                        id
                        heading
                        classification
                        frequencyCode
                        skillSet
                        actionsHtml
                        notesHtml
                        authorsNotesHtml
                        componentInstructionsHtml
                        taskType
                        timing
                    }
                }
            }
        `,
        { id, asOf },
        { returns: "getHistoricalMaintenanceCategory" }
    )
})

export const getMaintenanceCategories = api(async function getMaintenanceCategories({
    parentId,
    search,
    rootCategoryId,
}) {
    return query(
        gql`
            query GetMaintenanceCategories($parentId: String, $search: String, $rootCategoryId: String) {
                getMaintenanceCategories(parentId: $parentId, search: $search, rootCategoryId: $rootCategoryId) {
                    id
                    name
                    type
                    parentId
                    path
                    taskCount
                    aggregateTaskCount
                }
            }
        `,
        { parentId, search, rootCategoryId },
        { returns: "getMaintenanceCategories" }
    )
})

export const getHistoricalMaintenanceCategories = api(async function getHistoricalMaintenanceCategories({
    parentId,
    search,
    rootCategoryId,
    asOf,
} = {}) {
    if (!asOf) return []

    return query(
        gql`
            query GetHistoricalMaintenanceCategories(
                $parentId: String
                $search: String
                $rootCategoryId: String
                $asOf: String!
            ) {
                getHistoricalMaintenanceCategories(
                    parentId: $parentId
                    search: $search
                    rootCategoryId: $rootCategoryId
                    asOf: $asOf
                ) {
                    id
                    name
                    type
                    parentId
                    path
                    taskCount
                    aggregateTaskCount
                }
            }
        `,
        { parentId, search, rootCategoryId, asOf },
        { returns: "getHistoricalMaintenanceCategories" }
    )
})

export const getMaintenanceDirectories = api(async function getMaintenanceDirectories() {
    return query(
        gql`
            query GetMaintenanceDirectories {
                getMaintenanceDirectories {
                    id
                    name
                    type
                    parentId
                    path
                }
            }
        `,
        {},
        { returns: "getMaintenanceDirectories" }
    )
})

export const getUnmappedMaintenanceDocuments = api(async function getUnmappedMaintenanceDocuments() {
    return query(
        gql`
            query GetUnmappedMaintenanceDocuments {
                getUnmappedMaintenanceDocuments {
                    id
                    name
                    type
                    parentId
                    path
                    taskCount
                    originalText
                }
            }
        `,
        {},
        { returns: "getUnmappedMaintenanceDocuments" }
    )
})

export const getMaintenanceTaskHistory = api(async function getMaintenanceTaskHistory(taskId) {
    if (!taskId || taskId === "new") return []

    return query(
        gql`
            query GetMaintenanceTaskHistory($taskId: ID!) {
                getMaintenanceTaskHistory(taskId: $taskId) {
                    id
                    taskId
                    version
                    createdAt
                    firstSavedAt
                    entryType
                    rolledBackToVersion
                    changedFields
                    approvedAt
                    approvedBy
                    author {
                        id
                        displayName
                        photoURL
                    }
                    snapshot {
                        id
                        categoryId
                        categoryPath
                        heading
                        classification
                        frequency {
                            period
                            interval
                            label
                        }
                        frequencyCode
                        skillSet
                        minutes
                        unitOfMeasure
                        version
                        actionsHtml
                        notesHtml
                        authorsNotesHtml
                        componentInstructionsHtml
                        taskType
                        timing
                        draft
                        internalNotes {
                            text
                            timingAdjustment {
                                minutes
                                appliesToFrequencyCode
                                context
                            }
                        }
                        relationships {
                            type
                            target
                            note
                        }
                        compliance {
                            code
                            title
                            type
                        }
                        tags
                        warnings
                        scheduleReferences {
                            code
                            title
                        }
                        hyperlinksToAdd
                    }
                }
            }
        `,
        { taskId },
        { returns: "getMaintenanceTaskHistory" }
    )
})

export const getHistoricalMaintenanceTask = api(async function getHistoricalMaintenanceTask({ id, asOf } = {}) {
    if (!id || !asOf) return null

    return query(
        gql`
            query GetHistoricalMaintenanceTask($id: ID!, $asOf: String!) {
                getHistoricalMaintenanceTask(id: $id, asOf: $asOf) {
                    id
                    categoryId
                    categoryPath
                    heading
                    classification
                    frequency {
                        period
                        interval
                        label
                    }
                    frequencyCode
                    skillSet
                    minutes
                    unitOfMeasure
                    version
                    actionsHtml
                    notesHtml
                    authorsNotesHtml
                    componentInstructionsHtml
                    internalNotes {
                        text
                        timingAdjustment {
                            minutes
                            appliesToFrequencyCode
                            context
                        }
                    }
                    relationships {
                        type
                        target
                        note
                    }
                    compliance {
                        code
                        title
                        type
                    }
                    tags
                    taskType
                    timing
                    draft
                    warnings
                    scheduleReferences {
                        code
                        title
                    }
                    hyperlinksToAdd
                }
            }
        `,
        { id, asOf },
        { returns: "getHistoricalMaintenanceTask" }
    )
})

export const getHistoricalMaintenanceTasks = api(async function getHistoricalMaintenanceTasks({
    query: q,
    skip,
    take,
    orderStmt,
    search,
    categoryId,
    hasClassification,
    hasFrequency,
    hasSkillSet,
    asOf,
} = {}) {
    if (!asOf) return { items: [], totalCount: 0 }

    return query(
        gql`
            query GetHistoricalMaintenanceTasks(
                $query: JSON
                $skip: Int
                $take: Int
                $orderStmt: [String]
                $search: String
                $categoryId: String
                $hasClassification: Boolean
                $hasFrequency: Boolean
                $hasSkillSet: Boolean
                $asOf: String!
            ) {
                getHistoricalMaintenanceTasks(
                    query: $query
                    skip: $skip
                    take: $take
                    orderStmt: $orderStmt
                    search: $search
                    categoryId: $categoryId
                    hasClassification: $hasClassification
                    hasFrequency: $hasFrequency
                    hasSkillSet: $hasSkillSet
                    asOf: $asOf
                ) {
                    items {
                        id
                        categoryPath
                        heading
                        classification
                        frequencyCode
                        skillSet
                        minutes
                        unitOfMeasure
                        version
                        actionsHtml
                        notesHtml
                        authorsNotesHtml
                        componentInstructionsHtml
                        taskType
                        timing
                    }
                    totalCount
                }
            }
        `,
        { query: q, skip, take, orderStmt, search, categoryId, hasClassification, hasFrequency, hasSkillSet, asOf },
        { returns: "getHistoricalMaintenanceTasks" }
    )
})

export const getMaintenanceLibraryCheckpoints = api(async function getMaintenanceLibraryCheckpoints() {
    return query(
        gql`
            query GetMaintenanceLibraryCheckpoints {
                getMaintenanceLibraryCheckpoints {
                    id
                    label
                    createdAt
                    createdBy
                    taskCount
                    categoryCount
                }
            }
        `,
        {},
        { returns: "getMaintenanceLibraryCheckpoints" }
    )
})

export const getMaintenanceTaskElement = api(async function getMaintenanceTaskElement({ taskId, element } = {}) {
    if (!taskId || !element) return null

    return query(
        gql`
            query GetMaintenanceTaskElement($taskId: ID!, $element: String!) {
                getMaintenanceTaskElement(taskId: $taskId, element: $element) {
                    id
                    taskId
                    categoryId
                    categoryPath
                    taskHeading
                    element
                    elementLabel
                    label
                    valueType
                    valueText
                    valueJson
                }
            }
        `,
        { taskId, element },
        { returns: "getMaintenanceTaskElement" }
    )
})

export const resolveMaintenanceTaskElement = api(async function resolveMaintenanceTaskElement(id) {
    if (!id) return null

    return query(
        gql`
            query ResolveMaintenanceTaskElement($id: ID!) {
                resolveMaintenanceTaskElement(id: $id) {
                    id
                    taskId
                    categoryId
                    categoryPath
                    taskHeading
                    element
                    elementLabel
                    label
                    valueType
                    valueText
                    valueJson
                }
            }
        `,
        { id },
        { returns: "resolveMaintenanceTaskElement" }
    )
})

export const getMaintenanceTaskElementOptions = api(async function getMaintenanceTaskElementOptions() {
    return query(
        gql`
            query GetMaintenanceTaskElementOptions {
                getMaintenanceTaskElementOptions {
                    key
                    label
                    valueType
                }
            }
        `,
        {},
        { returns: "getMaintenanceTaskElementOptions" }
    )
})

export const putMaintenanceTask = api(async function putMaintenanceTask(task) {
    return mutate(
        gql`
            mutation PutMaintenanceTask($task: JSON!) {
                putMaintenanceTask(task: $task) {
                    id
                    categoryId
                    categoryPath
                    heading
                    classification
                    frequency {
                        period
                        interval
                        label
                    }
                    frequencyCode
                    skillSet
                    minutes
                    unitOfMeasure
                    version
                    actionsHtml
                    notesHtml
                    authorsNotesHtml
                    taskType
                    timing
                    draft
                }
            }
        `,
        { task },
        { returns: "putMaintenanceTask" }
    )
})

export const deleteMaintenanceTask = api(async function deleteMaintenanceTask(id) {
    return mutate(
        gql`
            mutation DeleteMaintenanceTask($id: ID!) {
                deleteMaintenanceTask(id: $id)
            }
        `,
        { id },
        { returns: "deleteMaintenanceTask" }
    )
})

export const putMaintenanceCategory = api(async function putMaintenanceCategory(category) {
    return mutate(
        gql`
            mutation PutMaintenanceCategory($category: JSON!) {
                putMaintenanceCategory(category: $category) {
                    id
                    name
                }
            }
        `,
        { category },
        { returns: "putMaintenanceCategory" }
    )
})

export const deleteMaintenanceCategory = api(async function deleteMaintenanceCategory(id) {
    return mutate(
        gql`
            mutation DeleteMaintenanceCategory($id: ID!) {
                deleteMaintenanceCategory(id: $id)
            }
        `,
        { id },
        { returns: "deleteMaintenanceCategory" }
    )
})

export const moveMaintenanceCategory = api(async function moveMaintenanceCategory(id, newParentId) {
    return mutate(
        gql`
            mutation MoveMaintenanceCategory($id: ID!, $newParentId: ID!) {
                moveMaintenanceCategory(id: $id, newParentId: $newParentId) {
                    id
                    parentId
                    path
                }
            }
        `,
        { id, newParentId },
        { returns: "moveMaintenanceCategory" }
    )
})

export const moveMaintenanceTask = api(async function moveMaintenanceTask(taskId, newCategoryId) {
    return mutate(
        gql`
            mutation MoveMaintenanceTask($taskId: ID!, $newCategoryId: ID!) {
                moveMaintenanceTask(taskId: $taskId, newCategoryId: $newCategoryId) {
                    id
                    categoryId
                }
            }
        `,
        { taskId, newCategoryId },
        { returns: "moveMaintenanceTask" }
    )
})

export const importMaintenanceLibrary = api(async function importMaintenanceLibrary(pack) {
    return mutate(
        gql`
            mutation ImportMaintenanceLibrary($pack: String!) {
                importMaintenanceLibrary(pack: $pack) {
                    totalTasks
                    tasksWithInternalNotes
                }
            }
        `,
        { pack },
        { returns: "importMaintenanceLibrary" }
    )
})

export const reprocessMaintenanceCategory = api(async function reprocessMaintenanceCategory(id, text) {
    return mutate(
        gql`
            mutation ReprocessMaintenanceCategory($id: ID!, $text: String!) {
                reprocessMaintenanceCategory(id: $id, text: $text)
            }
        `,
        { id, text },
        { returns: "reprocessMaintenanceCategory" }
    )
})

export const rollbackMaintenanceTask = api(async function rollbackMaintenanceTask(taskId, version) {
    return mutate(
        gql`
            mutation RollbackMaintenanceTask($taskId: ID!, $version: Int!) {
                rollbackMaintenanceTask(taskId: $taskId, version: $version) {
                    id
                    heading
                    version
                }
            }
        `,
        { taskId, version },
        { returns: "rollbackMaintenanceTask" }
    )
})
